import * as TabsPrimitive from "@rn-primitives/tabs"
import * as React from "react"
import { cn } from "@/lib/utils"
import { TextClassContext } from "@/components/reusables/ui/text"
import { BFPressable, type BFPressableProps } from "../buttons/CommonButtons"

const Tabs = TabsPrimitive.Root

export function useCurrentTabValue<T extends string = string>(): T {
  const { value } = TabsPrimitive.useRootContext()
  return value as T
}

const TabsList = React.forwardRef<
  TabsPrimitive.ListRef,
  TabsPrimitive.ListProps
>(({ className, ...props }, ref) => (
  <TabsPrimitive.List ref={ref} className={cn(className)} {...props} />
))
TabsList.displayName = TabsPrimitive.List.displayName

const TabsTrigger = React.forwardRef<
  TabsPrimitive.TriggerRef,
  TabsPrimitive.TriggerProps & BFPressableProps
>(({ className, children, ...rest }, ref) => {
  const { value } = TabsPrimitive.useRootContext()
  const {
    title,
    pressOnceOnly = false,
    pseudoDisabled = false,
    onPseudoPress,
    testID,
    loading,
    ...props
  } = rest
  return (
    <TextClassContext.Provider
      value={cn(value === props.value && "text-calm-blue")}
    >
      <TabsPrimitive.Trigger asChild ref={ref} {...props}>
        <BFPressable
          className={cn(
            "items-center self-stretch flex-1 w-full text-center",
            className,
            value === props.value && "border-b-2 border-calm-blue"
          )}
          pressOnceOnly={pressOnceOnly}
          pseudoDisabled={pseudoDisabled}
          onPseudoPress={onPseudoPress}
          title={title}
          testID={testID}
          loading={loading}
        >
          {({ pressed, hovered, loading, disabled, title }) => {
            let ChildrenNode = null
            if (typeof children === "function") {
              ChildrenNode = children({
                pressed,
                hovered,
                loading,
                disabled,
                title,
              })
            } else {
              ChildrenNode = children
            }
            return (
              <TextClassContext.Provider
                value={cn(
                  value === props.value && "text-calm-blue",
                  pressed && "opacity-50"
                )}
              >
                {ChildrenNode}
              </TextClassContext.Provider>
            )
          }}
        </BFPressable>
      </TabsPrimitive.Trigger>
    </TextClassContext.Provider>
  )
})
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName

const TabsContent = React.forwardRef<
  TabsPrimitive.ContentRef,
  TabsPrimitive.ContentProps
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content ref={ref} className={cn(className)} {...props} />
))
TabsContent.displayName = TabsPrimitive.Content.displayName

export { Tabs, TabsContent, TabsList, TabsTrigger }
